import { Stack, Typography } from '@chronosphereio/chrono-ui';
import { formatWithTimeZone, getFormattedTimeString, scaleLinear } from '@chronosphereio/core';
import { useMemo } from 'react';
import { useTimeZone } from '@/components';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { getAxisTimestamps, PER_LABEL_WIDTH_12, PER_LABEL_WIDTH_24 } from './utils/axis';

export { getAxisTimestamps };

interface AxisProps {
  startTime: number;
  endTime: number;
  width: number;
}

export const Axis = ({ startTime, endTime, width }: AxisProps) => {
  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();
  const perLabelWidth = timeFormat === '24h' ? PER_LABEL_WIDTH_24 : PER_LABEL_WIDTH_12;

  // Maps timestamps to pixel positions within the axis width
  const mapTimeToX = scaleLinear({ min: startTime, max: endTime }, { min: 0, max: width });

  const dates = useMemo(
    () => getAxisTimestamps({ minTimestamp: startTime, maxTimestamp: endTime, width, timeZone, perLabelWidth }),
    [endTime, startTime, timeZone, width, perLabelWidth]
  );

  const getTimeString = (date: string | number) => {
    const timeString = getFormattedTimeString({
      date,
      timeZone,
      timeFormat,
      omitTimeZoneAbbreviation: true,
      omitSeconds: true,
    });

    // At midnight, show the date (e.g. "Dec 05") instead of "00:00"
    const isMidnight = timeString === '00:00' || timeString === '12:00 AM';
    if (isMidnight) {
      return formatWithTimeZone(new Date(date), 'MMM dd', timeZone);
    }

    return timeString;
  };

  return (
    <Stack
      data-testid="axis"
      sx={{
        backgroundColor: (t) => t.palette.background.container,
        height: '30px',
      }}
    >
      <Stack
        direction="row"
        sx={{
          position: 'relative',
          flex: 1,
          alignItems: 'center',
          marginTop: 1,
          borderTop: (t) => `1px solid ${t.palette.border.default}`,
          justifyContent: 'space-between',
        }}
      >
        {dates.map((date) => (
          <Typography
            key={date}
            variant="caption"
            style={{
              left: `${mapTimeToX(date)}px`,
            }}
            sx={{
              position: 'absolute',
              transform: 'translate(-50%)',
              whiteSpace: 'nowrap',
            }}
          >
            {getTimeString(date)}
          </Typography>
        ))}
      </Stack>
    </Stack>
  );
};
